/** Building blocks shared by the settings sections: section cards, field labels, the save bar, notices, badges. */
import type { MessageDescriptor } from "@bundle/core";
import { Roles } from "@bundle/core";
import type { AccentColor, Member, Role } from "@bundle/contracts";
import type { ColorToken, IconName } from "@bundle/tokens";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Button, Card, Chip, Divider, Icon, IconButton, Pressable, Stack, Text, Toast, Toggle } from "../../primitives/index.ts";
import { useT, useTr } from "../../i18n/I18nProvider.tsx";

/** A titled card in a settings page. */
export function SettingsSection({ title, description, children, testID }: { title: string; description?: string; children?: ReactNode; testID: string }) {
  return (
    <Stack gap="3" testID={testID}>
      <Stack gap="0.5">
        <Text variant="headline" weight="semibold">
          {title}
        </Text>
        {description === undefined ? null : (
          <Text variant="footnote" color="text.secondary">
            {description}
          </Text>
        )}
      </Stack>
      <Card gap="4">{children}</Card>
    </Stack>
  );
}

export function FieldLabel({ label }: { label: string }) {
  return (
    <Text variant="footnote" weight="medium" color="text.secondary">
      {label}
    </Text>
  );
}

export function FieldNote({ text, tone = "neutral", testID }: { text: string; tone?: "neutral" | "danger" | "success" | "warning"; testID?: string }) {
  const color: ColorToken = tone === "danger" ? "status.danger" : tone === "success" ? "status.success" : tone === "warning" ? "status.warning" : "text.tertiary";
  return (
    <Text variant="caption" color={color} testID={testID}>
      {text}
    </Text>
  );
}

/**
 * A label over a value someone types elsewhere (a server name, a password shown once) with a copy button: mono,
 * selectable, wrapping, an optional caption under it. Without `onCopy` there's no button. Rows go in `CopyRows`.
 */
export function CopyRow(p: { label: string; value: string; caption?: string; onCopy?: (value: string) => void; copyLabel: string; testID: string }) {
  return (
    <Stack direction="horizontal" align="start" gap="2" paddingY="2.5" paddingX="3" testID={p.testID}>
      <Stack grow gap="0.5">
        <Text variant="footnote" color="text.secondary">
          {p.label}
        </Text>
        <Text mono selectable>
          {p.value}
        </Text>
        {p.caption === undefined ? null : (
          <Text variant="caption" color="text.tertiary">
            {p.caption}
          </Text>
        )}
      </Stack>
      {p.onCopy === undefined ? null : <IconButton icon="copy" size="sm" accessibilityLabel={p.copyLabel} onPress={() => p.onCopy!(p.value)} testID={`${p.testID}-copy`} />}
    </Stack>
  );
}

/** A group of `CopyRow`s: canvas background, subtle border and dividers. */
export function CopyRows({ children, testID }: { children: ReactNode[]; testID?: string }) {
  const rows = children.filter((c) => c !== null && c !== undefined && c !== false);
  return (
    <Stack background="bg.canvas" border="border.subtle" radius="md" testID={testID}>
      {rows.map((row, i) => (
        <Stack key={i}>
          {i === 0 ? null : <Divider />}
          {row}
        </Stack>
      ))}
    </Stack>
  );
}

/** "Unsaved changes · Discard · Save" under a form. */
export function SaveBar(p: { dirty: boolean; canSave: boolean; saving: boolean; onSave: () => void; onDiscard: () => void; testID: string }) {
  const t = useT();
  return (
    <Stack direction="horizontal" align="center" justify="end" gap="2" testID={p.testID}>
      {p.dirty ? (
        <Stack grow>
          <Text variant="footnote" color="text.secondary">
            {t("settings.unsaved")}
          </Text>
        </Stack>
      ) : null}
      {p.dirty ? <Button label={t("settings.discard")} variant="ghost" onPress={p.onDiscard} testID={`${p.testID}-discard`} /> : null}
      <Button label={p.saving ? t("settings.saving") : t("settings.save")} variant="primary" loading={p.saving} disabled={!p.canSave} onPress={p.onSave} testID={`${p.testID}-save`} />
    </Stack>
  );
}

/** The view model's last notice as a toast. */
export function SettingsNotice({ notice, onClose, testID }: { notice: MessageDescriptor | undefined; onClose: () => void; testID: string }) {
  const t = useT();
  const tr = useTr();
  return <Toast open={notice !== undefined} onClose={onClose} message={notice === undefined ? "" : tr(notice)} closeLabel={t("common.close")} durationMs={4000} testID={testID} />;
}

/** "Primary owner" / "Owner" / "Admin" pill (nothing for members and guests). */
export function RoleBadge({ member, primaryOwnerId }: { member: Member; primaryOwnerId: string | undefined }) {
  const tr = useTr();
  const badge = Roles.roleBadge(member, primaryOwnerId);
  if (badge === undefined) return null;
  return <Chip label={tr(badge)} tone="accent" icon={member.role === "owner" ? "key" : "shield"} testID={`role-badge-${member.userId}`} />;
}

/** A role's label ("Owner"). */
export function useRoleLabel(): (role: Role) => string {
  const tr = useTr();
  return (role) => tr(Roles.roleLabel(role));
}

/** A pill in a row of filters. */
export function FilterPill(p: { label: string; count?: number; selected: boolean; onPress: () => void; testID: string }) {
  return (
    <Pressable radius="full" selected={p.selected} onPress={p.onPress} testID={p.testID}>
      <Stack direction="horizontal" align="center" gap="1.5" paddingX="3" paddingY="1" radius="full" border={p.selected ? "accent.text" : "border.subtle"}>
        <Text variant="footnote" weight="medium" color={p.selected ? "text.link" : "text.secondary"}>
          {p.label}
        </Text>
        {p.count === undefined ? null : (
          <Text variant="caption" color="text.tertiary">
            {String(p.count)}
          </Text>
        )}
      </Stack>
    </Pressable>
  );
}

/** Avatar tone swatches (`avatar.1`…`avatar.8`). */
export const TONE_COLORS: ColorToken[] = ["avatar.1", "avatar.2", "avatar.3", "avatar.4", "avatar.5", "avatar.6", "avatar.7", "avatar.8"];

export function ToneSwatches({ value, onChange, testID }: { value: number; onChange: (tone: number) => void; testID: string }) {
  const t = useT();
  return (
    <Stack direction="horizontal" gap="2" wrap testID={testID}>
      {TONE_COLORS.map((color, i) => (
        <Pressable key={color} radius="full" selected={value === i + 1} onPress={() => onChange(i + 1)} accessibilityLabel={t("profile.avatar.colorOption", { n: i + 1 })} testID={`${testID}-${i + 1}`}>
          <Stack padding="0.5" radius="full" border={value === i + 1 ? "accent.text" : "border.subtle"}>
            <Stack width="avatar.sm" height="avatar.sm" radius="full" background={color} />
          </Stack>
        </Pressable>
      ))}
    </Stack>
  );
}

/**
 * Something signed in to my account that I can sign out (design system DeviceRow): a browser or app session, or an
 * app password. Icon tile, the name (one line) with an optional badge, a caption line, and a ghost button. Without
 * `actionLabel` there's no button (this device).
 */
export function DeviceRow(p: {
  icon: IconName;
  title: string;
  badge?: string;
  meta: string;
  actionLabel?: string;
  onAction?: () => void;
  /** This row's action is running (its button spins). */
  busy?: boolean;
  /** Another row's action is running. */
  disabled?: boolean;
  testID: string;
}) {
  return (
    <Stack direction="horizontal" align="center" gap="3" testID={p.testID}>
      <Stack padding="2" radius="md" background="bg.hover">
        <Icon name={p.icon} size="icon.md" color="text.secondary" />
      </Stack>
      <Stack grow gap="0.5">
        <Stack direction="horizontal" align="center" gap="2">
          <Text weight="medium" lines={1}>
            {p.title}
          </Text>
          {p.badge === undefined ? null : (
            <Text variant="caption" weight="semibold" color="status.success" testID={`${p.testID}-badge`}>
              {p.badge}
            </Text>
          )}
        </Stack>
        <Text variant="caption" color="text.tertiary" lines={1}>
          {p.meta}
        </Text>
      </Stack>
      {p.actionLabel === undefined ? null : (
        <Button label={p.actionLabel} variant="ghost" size="sm" loading={p.busy === true} disabled={p.disabled === true} onPress={() => p.onAction?.()} testID={`${p.testID}-action`} />
      )}
    </Stack>
  );
}

/** Loading / failed placeholder for a section. */
export function SectionStatus({ status, onRetry, testID }: { status: "loading" | "error"; onRetry: () => void; testID: string }) {
  const t = useT();
  return (
    <Stack align="center" gap="3" padding="6" testID={testID}>
      <Text color="text.secondary">{status === "loading" ? t("common.loading") : t("settings.loadFailed")}</Text>
      {status === "error" ? <Button label={t("common.retry")} variant="secondary" icon="reload" onPress={onRetry} testID={`${testID}-retry`} /> : null}
    </Stack>
  );
}

/**
 * A group of setting rows (Settings redesign): a small uppercase title with an optional hint or action on the right,
 * the rows in one bordered card with dividers, and an optional footnote under it.
 */
export function SettingsGroup(p: { title: string; hint?: string; action?: { label: string; onPress: () => void; testID: string }; foot?: string; children: ReactNode; testID: string }) {
  const rows = (Array.isArray(p.children) ? p.children.flat() : [p.children]).filter((c) => c !== null && c !== undefined && c !== false);
  return (
    <Stack gap="2" testID={p.testID}>
      <Stack direction="horizontal" align="center" justify="between" gap="2">
        <Text variant="caption" weight="semibold" color="text.tertiary">
          {p.title.toLocaleUpperCase()}
        </Text>
        {p.action !== undefined ? (
          <Button label={p.action.label} variant="ghost" size="sm" onPress={p.action.onPress} testID={p.action.testID} />
        ) : p.hint !== undefined ? (
          <Text variant="caption" color="text.tertiary" lines={1}>
            {p.hint}
          </Text>
        ) : null}
      </Stack>
      <Stack border="border.subtle" radius="lg">
        {rows.map((row, i) => (
          <Stack key={i}>
            {i === 0 ? null : <Divider />}
            {row}
          </Stack>
        ))}
      </Stack>
      {p.foot === undefined ? null : (
        <Text variant="footnote" color="text.tertiary">
          {p.foot}
        </Text>
      )}
    </Stack>
  );
}

/** One row of a `SettingsGroup`: label (optional tag pill), an optional line under it, and a trailing control. */
export function SettingsRow(p: { label: string; sub?: string; tag?: { label: string; tone?: "accent" | "success" | "neutral" }; trailing?: ReactNode; testID: string }) {
  return (
    <Stack direction="horizontal" align="center" gap="4" paddingX="4" paddingY="2.5" testID={p.testID}>
      <Stack grow gap="0.5">
        <Stack direction="horizontal" align="center" gap="2" wrap>
          <Text>{p.label}</Text>
          {p.tag === undefined ? null : <Chip label={p.tag.label} tone={p.tag.tone === "success" ? "success" : p.tag.tone === "accent" ? "accent" : "neutral"} />}
        </Stack>
        {p.sub === undefined ? null : (
          <Text variant="footnote" color="text.tertiary">
            {p.sub}
          </Text>
        )}
      </Stack>
      {p.trailing === undefined ? null : <Stack fixedSize>{p.trailing}</Stack>}
    </Stack>
  );
}

/** A brief "Saved" flag after a change lands: `flash()` shows it for 1.8 s. */
export function useSavedFlash(): [boolean, () => void] {
  const [shown, setShown] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = useCallback(() => {
    clearTimeout(timer.current);
    setShown(true);
    timer.current = setTimeout(() => setShown(false), 1800);
  }, []);
  return [shown, flash];
}

/** The "✓ Saved" mark next to a control that just saved (announced politely). */
export function SavedMark({ shown, testID }: { shown: boolean; testID: string }) {
  const t = useT();
  if (!shown) return null;
  return (
    <Stack direction="horizontal" align="center" gap="1" testID={testID}>
      <Icon name="check" size="icon.sm" color="status.success" />
      <Text variant="caption" weight="medium" color="status.success">
        {t("settings.saved.check")}
      </Text>
    </Stack>
  );
}

const ACCENTS: readonly AccentColor[] = ["indigo", "teal", "orange", "purple"];

/**
 * The accent colour swatches (Settings → Appearance): round chips in each set's colour, the chosen one ringed and ticked.
 * The app applies the pick at once, so the whole UI (and this row) changes colour live.
 */
export function AccentSwatches({ value, onChange, testID }: { value: AccentColor; onChange: (a: AccentColor) => void; testID: string }) {
  const t = useT();
  return (
    <Stack direction="horizontal" gap="2" align="center" testID={testID}>
      {ACCENTS.map((a) => {
        const on = a === value;
        return (
          <Pressable key={a} onPress={() => onChange(a)} pressed={on} radius="full" accessibilityLabel={t(`settings.appearance.accent.${a}`)} tooltip={t(`settings.appearance.accent.${a}`)} testID={`${testID}-${a}`}>
            <Stack width="avatar.md" height="avatar.md" radius="full" padding="0.5" border={on ? "text.primary" : "border.subtle"} align="center" justify="center">
              <Stack width="avatar.sm" height="avatar.sm" radius="full" background={`accent.swatch.${a}`} align="center" justify="center">
                {on ? <Icon name="check" size="icon.sm" color="accent.fg" /> : null}
              </Stack>
            </Stack>
          </Pressable>
        );
      })}
    </Stack>
  );
}

/** A `SettingsRow` with a switch that shows "Saved" once the change is through (`onChange` may return a promise). */
export function SettingsToggleRow(p: { label: string; sub?: string; value: boolean; disabled?: boolean; onChange: (on: boolean) => unknown; testID: string }) {
  const [shown, flash] = useSavedFlash();
  const change = (on: boolean) => {
    void Promise.resolve(p.onChange(on)).then(flash, () => {});
  };
  return (
    <SettingsRow
      label={p.label}
      {...(p.sub === undefined ? {} : { sub: p.sub })}
      testID={`${p.testID}-row`}
      trailing={
        <Stack direction="horizontal" align="center" gap="3">
          <SavedMark shown={shown} testID={`${p.testID}-saved`} />
          <Toggle value={p.value} onChange={change} accessibilityLabel={p.label} {...(p.disabled === undefined ? {} : { disabled: p.disabled })} testID={p.testID} />
        </Stack>
      }
    />
  );
}

/** A settings page's heading: title and the line under it. */
export function SettingsPageHeader({ title, description, testID }: { title: string; description?: string; testID: string }) {
  return (
    <Stack gap="1" testID={testID}>
      <Text variant="title2" weight="bold" heading={1}>
        {title}
      </Text>
      {description === undefined ? null : <Text color="text.secondary">{description}</Text>}
    </Stack>
  );
}
